import { useEffect, useMemo, useState } from 'react';

import {
  ChevronRight,
  InsertChartOutlined,
  PaidOutlined,
  SwapHorizOutlined,
} from '@mui/icons-material';

import {
  Avatar,
  AvatarGroup,
  Card,
  CardActionArea,
  Grid,
  Icon,
  Stack,
  Typography,
} from '@mui/material';

import TransactionsCardStatsModal from '@/components/transactions/TransactionsCardStatsModal';
import { CoinIcon } from '@/components/wallets';

import { useGetTransactionsStatsQuery } from '@/store/api/services/transactions/transactions';
import { formatCurrency } from '@bitstopco/bitstop-theme/helpers';
import { skipToken } from '@reduxjs/toolkit/query';

import type { TransactionsStats, WalletCoin } from '@/types';
import type { DispenseAssetView } from '@/types/generated/athena';
import type { SvgIconComponent } from '@mui/icons-material';

type Props = { stats?: TransactionsStats; variant?: 'outlined' | 'contained' };

const TransactionsCardStats = ({ stats: statsData, variant }: Props) => {
  const [selectedCoins, setSelectedCoins] = useState<WalletCoin[] | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<DispenseAssetView[] | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { data, isLoading } = useGetTransactionsStatsQuery(statsData ? skipToken : {});

  const allCoins = useMemo(
    () =>
      (data?.data ?? statsData)?.coins
        .filter(({ total_transactions }) => total_transactions > 0)
        .map(({ coin }) => coin) ?? [],
    [data, statsData],
  );

  const allAssets = useMemo(
    () =>
      (data?.data ?? statsData)?.assets
        ?.filter(({ total_transactions }) => total_transactions > 0)
        .map(({ asset }) => asset) ?? [],
    [data, statsData],
  );

  useEffect(() => {
    if ((selectedCoins === null && !isLoading) || statsData) {
      setSelectedCoins(allCoins);
    }
    if ((selectedAssets === null && !isLoading) || statsData) {
      setSelectedAssets(allAssets);
    }
  }, [allCoins, allAssets, isLoading, statsData]);

  const toggleCoin = (coin: WalletCoin) =>
    setSelectedCoins((prev) => {
      if (!prev) return [coin];
      const exists = prev.some((c) => c.id === coin.id);
      const updated = exists ? prev.filter((c) => c.id !== coin.id) : [...prev, coin];
      return allCoins.filter((c) => updated.some((uc) => uc.id === c.id));
    });

  const toggleAsset = (asset: DispenseAssetView) =>
    setSelectedAssets((prev) => {
      if (!prev) return [asset];
      const exists = prev.some((a) => a.id === asset.id);
      const updated = exists ? prev.filter((a) => a.id !== asset.id) : [...prev, asset];
      return allAssets.filter((a) => updated.some((ua) => ua.id === a.id));
    });

  const { totalFees, totalSales, totalTransactions, selectedCoinStats, selectedAssetStats } =
    useMemo(() => {
      const stats = data?.data ?? statsData;
      if (!stats || !selectedCoins || !selectedAssets)
        return {
          totalFees: '-',
          totalTransactions: '-',
          totalSales: '-',
          selectedCoinStats: [],
          selectedAssetStats: [],
        };
      const selectedCoinsIds = selectedCoins.map(({ id }) => id);
      const selectedCoinStats = stats.coins.filter(({ coin_id }) =>
        selectedCoinsIds.includes(coin_id),
      );
      const selectedAssetIds = selectedAssets.map(({ id }) => id);
      const selectedAssetStats = stats.assets.filter(({ asset_id }) =>
        selectedAssetIds.includes(asset_id),
      );

      const coinSales = selectedCoinStats.reduce((prev, { total_sales }) => prev + total_sales, 0);
      const assetSales = selectedAssetStats.reduce(
        (prev, { total_sales }) => prev + total_sales,
        0,
      );
      const totalSales = formatCurrency(coinSales + assetSales);

      const coinFees = selectedCoinStats.reduce((prev, { total_fees }) => prev + total_fees, 0);
      const assetFees = selectedAssetStats.reduce((prev, { total_fees }) => prev + total_fees, 0);
      const totalFees = formatCurrency(coinFees + assetFees);

      const coinTransactions = selectedCoinStats.reduce(
        (prev, { total_transactions }) => prev + total_transactions,
        0,
      );
      const assetTransactions = selectedAssetStats.reduce(
        (prev, { total_transactions }) => prev + total_transactions,
        0,
      );
      const totalTransactions = String(coinTransactions + assetTransactions);

      return { totalSales, totalFees, totalTransactions, selectedCoinStats, selectedAssetStats };
    }, [data, selectedCoins, selectedAssets, statsData]);

  const handleToggleModal = () => setShowModal(!showModal);

  return (
    <>
      {showModal && (
        <TransactionsCardStatsModal
          controlled={!statsData}
          allCoins={allCoins}
          allAssets={allAssets}
          onSelectCoin={toggleCoin}
          onSelectAsset={toggleAsset}
          onClose={handleToggleModal}
          selectedCoins={selectedCoins ?? []}
          selectedAssets={selectedAssets ?? []}
          selectedCoinStats={selectedCoinStats}
          selectedAssetStats={selectedAssetStats}
          totalTransactions={totalTransactions}
          totalSales={totalSales}
          totalFees={totalFees}
        />
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={PaidOutlined}
            coins={selectedCoins ?? []}
            assets={selectedAssets ?? []}
            onClick={handleToggleModal}
            label="Total Sales"
            stat={totalSales}
            variant={variant}
            data-test="stat-card-total-sales"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={InsertChartOutlined}
            coins={selectedCoins ?? []}
            assets={selectedAssets ?? []}
            onClick={handleToggleModal}
            label="Total Fees"
            stat={totalFees}
            data-test="stat-card-total-fees"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={SwapHorizOutlined}
            coins={selectedCoins ?? []}
            assets={selectedAssets ?? []}
            onClick={handleToggleModal}
            label="Total Transactions"
            stat={totalTransactions}
            data-test="stat-card-total-transactions"
          />
        </Grid>
      </Grid>
    </>
  );
};

const StatCard = ({
  onClick,
  label,
  stat,
  coins,
  assets,
  icon,
  variant = 'contained',
  'data-test': dataTest,
}: {
  coins: WalletCoin[];
  assets: DispenseAssetView[];
  onClick: () => void;
  label: string;
  stat: string;
  icon: SvgIconComponent;
  variant?: 'outlined' | 'contained';
  'data-test'?: string;
}) => {
  const color = variant === 'contained' ? 'background.gray' : undefined;
  return (
    <Card data-test={dataTest} sx={{ bgcolor: color, borderColor: color }}>
      <CardActionArea sx={{ p: 2 }} onClick={onClick}>
        <Stack direction="row" gap={2} alignItems="center">
          <Stack
            color="white"
            height={56}
            width={56}
            minWidth={56}
            bgcolor="text.secondary"
            borderRadius={999}
            alignItems="center"
            justifyContent="center"
          >
            <Icon component={icon} fontSize="large" />
          </Stack>
          <Stack minWidth={0}>
            <Typography variant="overline" noWrap textOverflow="ellipsis" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h6">{stat}</Typography>
          </Stack>
          <Stack
            flex={1}
            gap={0.5}
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <AvatarGroup max={999} sx={{ '.MuiAvatar-root': { borderColor: color } }}>
              {coins.map((coin) => (
                <Avatar sx={{ width: 24, height: 24 }} key={coin.id}>
                  <CoinIcon width={24} height={24} coin={coin} hideChain />
                </Avatar>
              ))}
              {assets.map((asset) => (
                <Avatar sx={{ width: 24, height: 24 }} key={asset.id}>
                  <CoinIcon width={24} height={24} asset={asset} hideChain />
                </Avatar>
              ))}
            </AvatarGroup>
            <ChevronRight color="action" />
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default TransactionsCardStats;
